import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { outputLanguageOptions } from "@/constants/outputLanguages";

import { classNames } from "@/utils/classNames";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { SenderPhraseSet } from "@/pages/Private/redux/senderCopywriting/senderCopywriting.schema";

import { TokenInput } from "./TokenInput";
import {
	applyFallbackLanguage,
	applyPhrase,
	applyPrimaryLanguage,
	phraseSetOf,
	phraseTabsOf,
	SenderLanguageValue,
	toggleSecondaryLanguage,
} from "./senderLanguage.utils";

interface SenderLanguageFieldsProps {
	value: SenderLanguageValue;
	onChange: (value: SenderLanguageValue) => void;
	/**
	 * Raises the portalled AutoComplete list above a host overlay. The onboarding
	 * tour shell sits at `z-[99990]`; without this the list paints behind it.
	 */
	dropdownZIndexClassName?: string;
}

/** The 24 EU languages are too many to scan in a plain select — offer them as a typeahead. */
const languageItems: AutoCompleteItem[] = outputLanguageOptions.map((option) => ({
	id: String(option.value),
	title: String(option.title),
}));

const languageName = (code: string): string =>
	String(outputLanguageOptions.find((option) => option.value === code)?.title ?? code);

/**
 * The sender's copywriting languages and phrasings, as a controlled form
 * (PREC-3686). Shared by the onboarding tour's LANGUAGE step and the
 * "Copywriting settings" card in Personal Settings so the two can't drift —
 * the caller owns loading and persisting, this owns the editing rules.
 */
export const SenderLanguageFields: FunctionComponent<SenderLanguageFieldsProps> = ({
	value,
	onChange,
	dropdownZIndexClassName,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`senderLanguage.${key}`);

	const phraseTabs = useMemo(() => phraseTabsOf(value), [value]);
	const [activeTab, setActiveTab] = useState<string>("");

	useEffect(() => {
		if (phraseTabs.length > 0 && !phraseTabs.includes(activeTab)) {
			setActiveTab(phraseTabs[0]);
		}
	}, [phraseTabs, activeTab]);

	const activeSet = phraseSetOf(value, activeTab);

	const updatePhrase = (field: keyof SenderPhraseSet, phrases: string[]) =>
		onChange(applyPhrase(value, activeTab, field, phrases));

	return (
		<div className="flex flex-col gap-5">
			<div className="flex flex-col gap-3">
				<span className="text-sm font-semibold text-text">{ts("languagesTitle")}</span>

				<div className="grid grid-cols-1 gap-3 tb:grid-cols-2">
					<AutoComplete
						data={languageItems}
						dropdownZIndexClassName={dropdownZIndexClassName}
						// AutoComplete only renders its search input when a search handler is passed;
						// the option list is static here, so filtering stays local to the component.
						handleSearch={() => undefined}
						handleSelect={(item) =>
							onChange(applyPrimaryLanguage(value, item ? String(item.id) : ""))
						}
						label={ts("primaryLabel")}
						selectedItem={value.primaryLanguage ? languageName(value.primaryLanguage) : ""}
					/>
					<AutoComplete
						data={languageItems}
						dropdownZIndexClassName={dropdownZIndexClassName}
						handleSearch={() => undefined}
						handleSelect={(item) =>
							onChange(applyFallbackLanguage(value, item ? String(item.id) : ""))
						}
						label={ts("fallbackLabel")}
						selectedItem={value.fallbackLanguage ? languageName(value.fallbackLanguage) : ""}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-semibold text-text">{ts("secondaryLabel")}</span>
					<div className="flex flex-wrap gap-2">
						{outputLanguageOptions.map((option) => {
							const code = String(option.value);
							const isPrimary = code === value.primaryLanguage;
							const active = value.secondaryLanguages.includes(code);

							return (
								<button
									key={code}
									aria-pressed={active}
									className={classNames(
										"rounded-full border px-3 py-1 text-sm font-medium duration-200",
										isPrimary
											? "cursor-not-allowed border-border-light bg-background-light text-text-meta opacity-50"
											: active
												? "cursor-pointer border-brand-light bg-blue-action-200 text-brand-light"
												: "cursor-pointer border-border-light bg-white text-text-meta hover:border-brand-light"
									)}
									disabled={isPrimary}
									title={isPrimary ? ts("alreadyPrimary") : undefined}
									type="button"
									onClick={() => onChange(toggleSecondaryLanguage(value, code))}
								>
									{option.title}
								</button>
							);
						})}
					</div>
				</div>
			</div>

			{phraseTabs.length > 0 && (
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-0.5">
						<span className="text-sm font-semibold text-text">{ts("phrasingsTitle")}</span>
						<span className="text-xs text-text-meta">{ts("phrasingsDesc")}</span>
					</div>

					<div className="flex gap-1.5">
						{phraseTabs.map((code) => (
							<button
								key={code}
								className={classNames(
									"cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium duration-200",
									code === activeTab
										? "bg-blue-action-200 text-brand-light"
										: "text-text-meta hover:bg-background-light"
								)}
								type="button"
								onClick={() => setActiveTab(code)}
							>
								{languageName(code)}
							</button>
						))}
					</div>

					<div className="flex flex-col gap-3 rounded-xl border border-border-light bg-white p-4">
						<TokenInput
							label={ts("openersLabel")}
							placeholder={ts("addPlaceholder")}
							values={activeSet.openers}
							onChange={(phrases) => updatePhrase("openers", phrases)}
						/>
						<TokenInput
							label={ts("closingsLabel")}
							placeholder={ts("addPlaceholder")}
							values={activeSet.closings}
							onChange={(phrases) => updatePhrase("closings", phrases)}
						/>
						<TokenInput
							label={ts("ctasLabel")}
							placeholder={ts("addPlaceholder")}
							values={activeSet.ctas}
							onChange={(phrases) => updatePhrase("ctas", phrases)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};
